import {Injectable} from '@angular/core';
// rxjs
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {tap, map, filter} from 'rxjs/operators';
// services
import {RestaurantService} from '../services/restaurant.service';
import {RouteService} from '../services/route.service';
// models
import {
  Restaurant,
  RestaurantAvailableStatus,
  Taxes, Country, Group, CustomCss, TimeRange, RangeParam, ItemFilter,
  Categories,
  RewardProduct, WorkDay
} from '../models/restaurant.model';
import {FormatDataService} from '../services/format-data.service';
import {FormatStyleService} from '../services/format-style.service';
import {ActivatedRoute, Router} from '@angular/router';
import {PaymentStore} from './payment.store';
import { UtilService } from '../services/util.service';
import { StorageService } from '../services/storage.service';
import { UrlParam } from '../enums/group-param.enum';
import { KioskSmsOrder } from '../models/payment-item.model';
import { Category, Item } from '../models/catalog.model';
import { DeliveryTypeItem, FulfillmentTypeItem } from '../../pages/checkout/models/location.model';
import { FulfillmentType, DeliveryType } from '../models/order.model';
import { LANDING_URL, TABLE_LOCATION_KEY } from '../constants';

@Injectable()
export class RestaurantStore {
  // restaurant's state properties
  private _restaurants$ = new BehaviorSubject<Restaurant[]>([]);
  private _categories$ = new BehaviorSubject<Categories[]>([]);
  private _restaurantAvailableStatus$ = new BehaviorSubject<RestaurantAvailableStatus>(new RestaurantAvailableStatus());
  private _estimateDeliveryTime$ = new BehaviorSubject<number>(0);
  private _group$ = new BehaviorSubject<Group>(null);
  private _isGroup$ = new BehaviorSubject<boolean>(false);
  private _curCategories$ = new BehaviorSubject<Categories[]>([]);
  private _curProducts$ = new BehaviorSubject<Item[][]>([]);
  private _restaurantCountryInfo$ = new BehaviorSubject<Country>(new Country());
  private _filteredWorkHours$ = new BehaviorSubject<any>(null);
  private _isDefaultTax$ = new BehaviorSubject<boolean>(false);
  private _restaurantIsAvailable$ = new BehaviorSubject<boolean>(true);
  private _hideRestaurantOrdersAhead$ = new BehaviorSubject<boolean>(false);
  private _openItemCardInProgress$ = new BehaviorSubject<boolean>(false);
  private _timezone = new BehaviorSubject<string>(null);
  private _enabledFulfillments = new BehaviorSubject<string[]>([]);
  private _fulfillmentParams = new BehaviorSubject<string[]>([]);
  private _timeRangeParams = new BehaviorSubject<TimeRange>(null);
  private _discountPromo = new BehaviorSubject<string>(null);
  private _subscriptionCode = new BehaviorSubject<string>(null);
  private _updateSubscriptionHistory$ = new BehaviorSubject<boolean>(false);
  private _disableTodayOrders$ = new BehaviorSubject<boolean>(false);
  private _activeLocationIndex$ = new BehaviorSubject<number>(0);
  private _itemColumns$ = new BehaviorSubject<number>(null);
  private _updateGroupInfo$ = new BehaviorSubject<boolean>(false);
  private _updatePhoneNumber$ = new BehaviorSubject<boolean>(false);
  private _loyaltyPointsName$ = new BehaviorSubject<any>(null);
  private _restaurantChanged$ = new BehaviorSubject<any>(false);
  private _pickupLocationChanges$ = new BehaviorSubject<any>(null);
  private _selectedLocationInGroup = new BehaviorSubject<string>(null);
  private _groupIdForRedirect = new BehaviorSubject<string>(null);
  private _isGoToCheckout = new BehaviorSubject<boolean>(false);
  private _isRestaurantsLoaded: Subject<boolean> = new Subject<boolean>();
  private _isKioskSmsOrder = new BehaviorSubject<boolean>(false);
  private _kioskSmsOrderData = new BehaviorSubject<KioskSmsOrder>(null);
  private _catalogIsEmpty: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _rewardProducts: BehaviorSubject<RewardProduct[]> = new BehaviorSubject<RewardProduct[]>([]);
  private _loadItemsInProgress$ = new BehaviorSubject<boolean>(false);
  private _lastRestaurantsId: string = null;
  private _lastRestaurantsIsGroup: boolean = false;

  // Fulfillment Type Items (shared across components)
  public readonly pickupFulfillmentType: FulfillmentTypeItem = new FulfillmentTypeItem(
    'Pickup',
    'icon-pickup',
    true,
    FulfillmentType.PICKUP,
    0
  );
  public readonly curbsideFulfillmentType: FulfillmentTypeItem = new FulfillmentTypeItem(
    'Curbside',
    'icon-curbside',
    true,
    FulfillmentType.CURBSIDE,
    0
  );
  public readonly tablesideFulfillmentType: FulfillmentTypeItem = new FulfillmentTypeItem(
    'Tableside',
    'icon-tableside',
    true,
    FulfillmentType.TABLESIDE,
    0
  );
  public readonly deliveryFulfillmentType: FulfillmentTypeItem = new FulfillmentTypeItem(
    'Delivery',
    'icon-delivery',
    true,
    FulfillmentType.DELIVERY,
    0
  );
  public readonly fridgeFulfillmentType: FulfillmentTypeItem = new FulfillmentTypeItem(
    'Fridge',
    'icon-fridge',
    true,
    FulfillmentType.FRIDGE,
    0
  );
  public readonly handoffFulfillmentType: FulfillmentTypeItem = new FulfillmentTypeItem(
    'XSide',
    'icon-pickup',
    true,
    FulfillmentType.HANDOFF,
    0
  );
  public readonly giftcardFulfillmentType: FulfillmentTypeItem = new FulfillmentTypeItem(
    'GiftCard',
    'icon-gift-card',
    true,
    FulfillmentType.GIFTCARD,
    0
  );

  public get isDefaultTax$(): Observable<boolean> {
    return this._isDefaultTax$.asObservable();
  }

  public get restaurants$(): Observable<Restaurant[]> {
    return this._restaurants$.asObservable();
  }

  public get categories$(): Observable<Categories[]> {
    return this._categories$.asObservable();
  }

  public get categories(): Categories[] {
    return this._categories$.getValue();
  }

  public set categories(categories: Categories[]) {
    this._categories$.next(categories);
  }

  public get group$(): Observable<Group> {
    return this._group$.asObservable();
  }

  public get group(): Group {
    return this._group$.getValue();
  }

  public get catalogIsEmpty$(): Observable<boolean> {
    return this._catalogIsEmpty.asObservable();
  }

  public set catalogIsEmpty(isEmpty: boolean)  {
    this._catalogIsEmpty.next(isEmpty);
  }

  public set group(value: Group) {
    this._group$.next(value);
  }

  public get updateGroupInfo$(): Observable<boolean> {
    return this._updateGroupInfo$.asObservable();
  }

  public set updateGroupInfo(value: boolean) {
    this._updateGroupInfo$.next(value);
  }

  public get updatePhoneNumber$(): Observable<boolean> {
    return this._updatePhoneNumber$.asObservable();
  }

  public set updatePhoneNumber(value: boolean) {
    this._updatePhoneNumber$.next(value);
  }

  public get sortedRestaurants$(): Observable<Restaurant[]> {
    return new BehaviorSubject<Restaurant[]>(this._restaurants$.getValue()).asObservable();
  }

  public get restaurantAvailableStatus$(): Observable<RestaurantAvailableStatus> {
    return this._restaurantAvailableStatus$.asObservable();
  }

  public get curCategories$(): Observable<Categories[]> {
    return this._curCategories$.asObservable();
  }

  public get curProducts$(): Observable<Item[][]> {
    return this._curProducts$.asObservable();
  }

  public get filteredWorkHours$(): Observable<any> {
    return this._filteredWorkHours$.asObservable();
  }

  public get filteredWorkHours(): any {
    return this._filteredWorkHours$.getValue();
  }

  public updateFilteredWorkHours(workHours: any): void {
    this._filteredWorkHours$.next(workHours);
  }

  public get restaurantIsAvailable$(): Observable<boolean> {
    return this._restaurantIsAvailable$.asObservable();
  }

  public get disableTodayOrders(): boolean {
    return this._disableTodayOrders$.getValue();
  }

  public get itemColumns(): number {
    return this._itemColumns$.getValue();
  }

  public get openItemCardInProgress(): boolean {
    return this._openItemCardInProgress$.getValue();
  }

  public get curProducts(): Item[][] {
    return this._curProducts$.getValue();
  }

  public get restaurantCountryInfo(): Country {
    return this._restaurantCountryInfo$.getValue();
  }

  public set restaurantCountryInfo(value: Country) {
    this._restaurantCountryInfo$.next(value);
  }

  public get isGroup(): boolean {
    return this._isGroup$.value;
  }

  public set isGroup(value: boolean) {
    this._isGroup$.next(value);
  }

  public get loyaltyPointsName(): any {
    return this._loyaltyPointsName$.value;
  }

  public set loyaltyPointsName(value: any) {
    this._loyaltyPointsName$.next(value);
  }

  public get activeLocationIndex(): number {
    return this._activeLocationIndex$.getValue();
  }

  public set activeLocationIndex(value: number) {
    this._activeLocationIndex$.next(value);
  }

  get activeLocationIndex$(): Observable<number> {
    return this._activeLocationIndex$.asObservable();
  }

  public get estimateDeliveryTime(): number {
    return this._estimateDeliveryTime$.getValue();
  }

  public set estimateDeliveryTime(value: number) {
    this._estimateDeliveryTime$.next(value);
  }

  public get timezone(): string {
    return this._timezone.value;
  }

  public set timezone(value: string) {
    this._timezone.next(value);
  }

  public get enabledFulfillments(): string[] {
    return this._enabledFulfillments.value;
  }

  public set enabledFulfillments(value: string[]) {
    this._enabledFulfillments.next(value);
  }

  public get fulfillmentParams(): string[] {
    return this._fulfillmentParams.value;
  }

  public set fulfillmentParams(value: string[]) {
    this._fulfillmentParams.next(value);
  }

  public get timeRangeParams(): TimeRange {
    return this._timeRangeParams.value;
  }

  public set timeRangeParams(value: TimeRange) {
    this._timeRangeParams.next(value);
  }

  public get discountPromo(): string {
    return this._discountPromo.value;
  }

  public set discountPromo(value: string) {
    this._discountPromo.next(value);
  }

  public get subscriptionCode(): string {
    return this._subscriptionCode.value;
  }

  public set subscriptionCode(value: string) {
    this._subscriptionCode.next(value);
  }

  public set itemColumns(value: number) {
    this._itemColumns$.next(value);
  }

  public get pickupLocationChanged(): any {
    return this._pickupLocationChanges$.asObservable();
  }

  public get selectedLocationInGroup(): string {
    return this._selectedLocationInGroup.value;
  }

  public set selectedLocationInGroup(value: string) {
    this._selectedLocationInGroup.next(value);
  }

  public get groupIdForRedirect() {
    return this._groupIdForRedirect.value;
  }

  public set groupIdForRedirect(value: string) {
    this._groupIdForRedirect.next(value);
  }

  public get isGoToCheckout() {
    return  this._isGoToCheckout.value;
  }

  public set isGoToCheckout(value: boolean) {
    this._isGoToCheckout.next(value);
  }
  public getIsRestaurantLoaded(): Observable<boolean> {
    return this._isRestaurantsLoaded.asObservable();
  }
  public setIsRestaurantLoaded(value: boolean): void {
    this._isRestaurantsLoaded.next(value);
  }

  public get isKioskSmsOrder(): boolean {
    return this._isKioskSmsOrder.value;
  }

  public set isKioskSmsOrder(isKioskSmsOrder: boolean) {
    this._isKioskSmsOrder.next(isKioskSmsOrder);
  }

  public get kioskSmsOrderData(): KioskSmsOrder {
    return this._kioskSmsOrderData.getValue();
  }

  public set kioskSmsOrderData(value: KioskSmsOrder) {
    this._kioskSmsOrderData.next(value);
  }

  get rewardProducts(): any[] {
    return this._rewardProducts.value;
  }

  set rewardProducts(products: any[]) {
    this._rewardProducts.next(products);
  }

  public get loadItemsInProgress$(): Observable<boolean> {
    return this._loadItemsInProgress$.asObservable()
  }

  public get loadItemsInProgress(): boolean {
    return this._loadItemsInProgress$.value;
  }

  public set loadItemsInProgress(value: boolean) {
    this._loadItemsInProgress$.next(value);
  }

  constructor(
    private paymentStore: PaymentStore,
    private formatStyleService: FormatStyleService,
    private formatDataService: FormatDataService,
    private restaurantService: RestaurantService,
    private routeService: RouteService,
    private router: Router,
    private route: ActivatedRoute,
    private utilService: UtilService,
    private storageService: StorageService,
  ) {}

  public bootstrapRestaurants(id: string, isGroup?: boolean, locationId?: string): void {
    this._lastRestaurantsId = id;
    this._lastRestaurantsIsGroup = isGroup;
    const baseUrl = this.buildRestaurantsBaseUrl(id, isGroup);
    if (baseUrl && this._restaurants$.getValue().length === 0) {
      this.restaurantService.getRestaurants(baseUrl)
        .subscribe(async(restaurants: Restaurant[]) => {
          this._restaurants$.next(restaurants);
          let restaurant;
          if (locationId && restaurants?.length) {
            restaurant = restaurants.find(r => r.locationId === locationId) || restaurants[0];
          }
          if (!restaurant) {
            restaurant = restaurants[0];
          }
          this.setCustomCss(restaurant?.customCSS);
          this.recomputeFilteredWorkHours(restaurants);
          this.setIsRestaurantLoaded(true);
        });
        this.restaurantService.getLocationsByCategories(id).subscribe(async (response: Categories[]) => {
          this.categories = response;
        });
      return;
    }
    this.setIsRestaurantLoaded(true);
  }

  // Re-fetch venues/active and merge fresh status/hours into loaded restaurants without dropping menus
  public refreshRestaurants(id: string, isGroup?: boolean, locationId?: string): void {
    this._lastRestaurantsId = id;
    this._lastRestaurantsIsGroup = isGroup;
    const existing = this._restaurants$.getValue();
    if (existing.length === 0) {
      this.bootstrapRestaurants(id, isGroup, locationId);
      return;
    }
    const baseUrl = this.buildRestaurantsBaseUrl(id, isGroup);
    if (!baseUrl) {
      return;
    }
    this.restaurantService.getRestaurants(baseUrl)
      .subscribe((fresh: Restaurant[]) => {
        const merged = this.mergeRestaurantStatuses(this._restaurants$.getValue(), fresh);
        this._restaurants$.next(merged);
        this.recomputeFilteredWorkHours(merged);
        this.setIsRestaurantLoaded(true);
      });
  }

  // Re-run the last restaurants fetch, used on app resume / tab refocus
  public refreshCurrentRestaurants(): void {
    if (!this._lastRestaurantsId || this._restaurants$.getValue().length === 0) {
      return;
    }
    this.refreshRestaurants(this._lastRestaurantsId, this._lastRestaurantsIsGroup);
  }

  private buildRestaurantsBaseUrl(id: string, isGroup?: boolean): string {
    let baseUrl = `merchants/${id}`.split('?')[0];
    if (isGroup) {
      baseUrl = `groups/${id}`;
    }
    if (this.isKioskSmsOrder) {
      baseUrl = `merchants/${id}`;
    }
    return baseUrl;
  }

  private mergeRestaurantStatuses(existing: Restaurant[], fresh: Restaurant[]): Restaurant[] {
    return fresh.map((freshRestaurant: Restaurant) => {
      const current = existing.find(r => r.locationId === freshRestaurant.locationId);
      if (current) {
        freshRestaurant.categories = current.categories;
        if (current.sellerDeliveryLocations?.length) {
          freshRestaurant.sellerDeliveryLocations = current.sellerDeliveryLocations;
        }
      }
      return freshRestaurant;
    });
  }

  private recomputeFilteredWorkHours(restaurants: Restaurant[]): void {
    const filteredWorkHours = {};
    for (const restaurant of restaurants) {
      filteredWorkHours[restaurant.locationId] = this.formatDataService.formatWorkHoursWithBlackout(restaurant.hoursOfOperation, restaurant.blackoutDateRanges, new Date());
    }
    this._filteredWorkHours$.next(filteredWorkHours);
  }

  public updateWorkHours(id: string, isGroup?: boolean): void {
    let baseUrl = `merchants/${id}`;
    if (isGroup) {
      baseUrl = `groups/${id}`;
    }
    if (baseUrl) {
      this.restaurantService.getRestaurants(baseUrl)
        .subscribe(async(restaurants: Restaurant[]) => {
          const filteredWorkHours = {};
          restaurants.forEach((restaurant: Restaurant) => {
            filteredWorkHours[restaurant.locationId] = this.formatDataService.formatWorkHoursWithBlackout(restaurant.hoursOfOperation, restaurant.blackoutDateRanges, new Date());
          });
          this._filteredWorkHours$.next(filteredWorkHours);
        });
    }
  }

  public getRestaurantById(id: string): Observable<Restaurant> {
    return this.restaurants$.pipe(
      map(restaurants => restaurants.find(item => item.locationId === id))
    );
  }

  public getCategoryVariationIds(products: Item[]): string[] {
    const variationIdList = [];
    for (const product of products) {
      for (const variation of product.variations) {
        if (variation.track_inventory) {
          variationIdList.push(variation.id);
        }
      }
    }
    return variationIdList;
  }

  public getUseInventoryItems(products: Item[]): string[] {
    const variationIdList = [];
    for (const product of products) {
      for (const variation of product.variations) {
        if (variation.track_inventory) {
          variationIdList.push(variation.id);
        }
      }
    }
    return variationIdList;
  }

  public getRestaurants(): Observable<Restaurant[]> {
    return this.restaurants$;
  }

  public getGroupById(groupId: string): Observable<Group> {
    const baseUrl = groupId ? `groups/${groupId}` : this.routeService.getBaseUrl();
    return this.restaurantService.getGroup(baseUrl);
  }

  public getGroupByCode(groupCode: string): Observable<Group> {
    const baseUrl = groupCode ? `groups/code/${groupCode}` : this.routeService.getBaseUrl();
    return this.restaurantService.getGroup(baseUrl);
  }

  public clearCurCategoriesAndCurProducts(): void {
    this._curCategories$.next([]);
    this._curProducts$.next([]);
  }

  public clearCurProducts(): void {
    this._curProducts$.next([]);
  }

  private isCorrectTax(taxes: Taxes[]): boolean {
    if (!taxes) return false;

    if (taxes instanceof Array && taxes.length) {
      for (const tax of taxes) {
        if (!this.isValidTaxField(tax)) return false;
      }
    } else return false;

    return true;
  }

  private isValidTaxField(tax) {
    for (const taxesKey in tax) {
      if (!tax[taxesKey]) return false;
    }
  }

  private setDefaultTaxValue(taxes: Taxes[] | null, restaurantId: string): Taxes[] {
    taxes.forEach(tax => {
      // tslint:disable-next-line:forin
      for (const taxKey in tax) {
        if (taxKey === 'id') {
          tax[taxKey] = tax[taxKey] || restaurantId;
        }
          tax[taxKey] = tax[taxKey] || 0;
      }
    });
    return taxes;
  }

  public setRestaurantIsAvailable(value: boolean) {
    this._restaurantIsAvailable$.next(value);
  }

  public set updateSubscriptionHistory(value: boolean) {
    this._updateSubscriptionHistory$.next(value);
  }

  public setRestaurantAvailableStatus(value: RestaurantAvailableStatus) {
    this._restaurantAvailableStatus$.next(value);
  }

  public set hideRestaurantOrdersAhead(value: boolean) {
    this._hideRestaurantOrdersAhead$.next(value);
  }

  public set disableTodayOrders(value: boolean) {
    this._disableTodayOrders$.next(value);
  }

  public set openItemCardInProgress(value: boolean) {
    this._openItemCardInProgress$.next(value);
  }

  public checkRouteContainsTimeRangeOptions(): void {
    if (this.route.snapshot.queryParams.startTime != null && this.route.snapshot.queryParams.startTime.length > 0 &&
        this.route.snapshot.queryParams.endTime != null && this.route.snapshot.queryParams.endTime.length > 0)  {
      const timeRange = new TimeRange(new Date(this.route.snapshot.queryParams.startTime), new Date(this.route.snapshot.queryParams.endTime));
      sessionStorage.setItem(RangeParam.RANGE_START_TIME, timeRange.startTime.toISOString());
      sessionStorage.setItem(RangeParam.RANGE_END_TIME, timeRange.endTime.toISOString());
      this.timeRangeParams = timeRange;
    } else if (sessionStorage.getItem(RangeParam.RANGE_START_TIME) != null && sessionStorage.getItem(RangeParam.RANGE_START_TIME).length > 0 &&
               sessionStorage.getItem(RangeParam.RANGE_END_TIME) != null && sessionStorage.getItem(RangeParam.RANGE_END_TIME).length > 0) {
      this.timeRangeParams = new TimeRange(new Date(sessionStorage.getItem(RangeParam.RANGE_START_TIME)), new Date(sessionStorage.getItem(RangeParam.RANGE_END_TIME)));
    }
  }

  public checkRouteContainsDiscountOption(): void {
    if (this.route.snapshot.queryParams.discount != null && this.route.snapshot.queryParams.discount.length > 0)  {
      sessionStorage.setItem(UrlParam.discount, this.route.snapshot.queryParams.discount);
      this.discountPromo = this.route.snapshot.queryParams.discount;
    } else if (sessionStorage.getItem(UrlParam.discount) != null && sessionStorage.getItem(UrlParam.discount).length > 0) {
      this.discountPromo = sessionStorage.getItem(UrlParam.discount);
    }
  }

  public checkCustomNumber(): void {
    if (this.route.snapshot.queryParams.customInstruction) {
      sessionStorage.setItem(UrlParam.customInstruction, decodeURIComponent(this.route.snapshot.queryParams.customInstruction));
    }
  }

  public checkRouteContainsFullfillmentOptions(): void {
    // Check the actual browser URL, not the stale snapshot
    const urlParams = new URLSearchParams(window.location.search);
    const urlParam = urlParams.get('fulfillments');
    if (urlParam != null && urlParam.length > 0) {
      // URL has the param - store it in state
      this.fulfillmentParams = urlParam.split(',');
    } else if (this.fulfillmentParams && this.fulfillmentParams.length > 0) {
      // URL doesn't have param but state does - add it back to URL
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { fulfillments: this.fulfillmentParams.join(',') },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    } else {
      // Neither URL nor state has it - clear state
      this.fulfillmentParams = [];
    }
  }

  public checkRouteContainsTableOptions(): void {
    if (this.route.snapshot.queryParams.tableNumber != null && this.route.snapshot.queryParams.tableNumber.length > 0) {
      sessionStorage.setItem(UrlParam.tableNumber, this.route.snapshot.queryParams.tableNumber);
    }
  }

  public checkRouteContainsTableNumber(tableNumber: string): void {
    const locationId = this.locationIdFromUrl();
    const tableLocationId = sessionStorage.getItem(TABLE_LOCATION_KEY);
    if (tableNumber && locationId && tableLocationId !== locationId && !this.isLandingTableLink(locationId, tableNumber)) {
      // Not a scan here: a table param carried over from another location's QR by in-app navigation.
      this.clearScannedTable();
      this.router.navigate([], { relativeTo: this.route, queryParams: { tableNumber: null }, queryParamsHandling: 'merge', replaceUrl: true });
      return;
    }
    this.checkRouteContainsTableOptions();
    if (tableNumber && !this.paymentStore.isLinkTableNumber) {
      this.fulfillmentParams = ['tableside', 'tabs'];
      this.paymentStore.tableNumber = tableNumber;
      this.paymentStore.isLinkTableNumber = !!tableNumber;
      sessionStorage.setItem(UrlParam.tableNumber, tableNumber);
      if (locationId) {
        sessionStorage.setItem(TABLE_LOCATION_KEY, locationId);
        this.storageService.set('currentRestaurantId', locationId);
      }
    } else if (sessionStorage.getItem(UrlParam.tableNumber) != null && sessionStorage.getItem(UrlParam.tableNumber).length > 0) {
      this.fulfillmentParams = ['tableside', 'tabs'];
      this.paymentStore.tableNumber = sessionStorage.getItem(UrlParam.tableNumber);
      this.paymentStore.isLinkTableNumber = !!sessionStorage.getItem(UrlParam.tableNumber);
    }
  }

  /** CF-32895: a scanned table belongs to the location whose QR set it; drop it anywhere else. */
  public keepTableNumberForLocation(locationId: string): void {
    const tableLocationId = sessionStorage.getItem(TABLE_LOCATION_KEY);
    if (!locationId || !tableLocationId || tableLocationId === locationId) {
      return;
    }
    this.clearScannedTable();
  }

  private clearScannedTable(): void {
    sessionStorage.removeItem(UrlParam.tableNumber);
    sessionStorage.removeItem(TABLE_LOCATION_KEY);
    this.paymentStore.clearTableNumber();
    this.fulfillmentParams = (this.fulfillmentParams || []).filter(param => param !== 'tableside' && param !== 'tabs');
    if (this.paymentStore.fulfilmentType === FulfillmentType.TABLESIDE) {
      this.paymentStore.setFulfilmentType(FulfillmentType.PICKUP);
    }
  }

  /** A ?tableNumber= QR opened straight onto this location (page load), as opposed to one added in-app. */
  private isLandingTableLink(locationId: string, tableNumber: string): boolean {
    const landing = new URL(LANDING_URL);
    return landing.pathname.includes(`/locations/${locationId}`) && landing.searchParams.get('tableNumber') === tableNumber;
  }

  private locationIdFromUrl(): string | null {
    const match = /\/locations\/([^/?#]+)/.exec(this.router.url || '');
    return match ? match[1] : null;
  }

  public checkRoutQueryParams(): void {
    this.route.queryParams.subscribe(params => {
      if (params && params.tableNumber) {
        this.checkRouteContainsTableNumber(params.tableNumber);
      }
    });
  }

  public findLocationById(restaurants: Restaurant[], locationId: string): Restaurant {
    return restaurants?.find((restaurant) => restaurant.locationId === locationId);
  }

  public backToStorePage(fromPayment?: boolean, url?: string) {
    if (this.isGroup || (url && url.includes('group'))) {
      const groupUrl = this.routeService.getGroupUrl(url);
      this.updateGroupInfo = true;
      this.router.navigate([groupUrl], { queryParamsHandling: 'merge', queryParams: this.stringToParams(url)})
        .catch(error => console.error(error));
    } else if (this.group || this.groupIdForRedirect) {
      const groupUrl = this.routeService.createGroupUrl(this.group?.groupId || this.groupIdForRedirect);
      this.updateGroupInfo = true;
      this.router.navigate([groupUrl], { queryParamsHandling: 'merge', queryParams: this.stringToParams(url) })
        .catch(error => console.log(error));
    } else {
      const locationUrl = this.routeService.getLocationUrl(url);
      if (fromPayment) {
        this.router.navigate([locationUrl], {queryParamsHandling: 'merge' , queryParams: {fromPayment: true}})
          .catch(error => console.error(error));
      } else {
        this.router.navigate([locationUrl], { queryParamsHandling: 'merge', queryParams: this.stringToParams(url) })
          .catch(error => console.error(error));
      }
    }
  }

  public getRestaurantFulfillment(restaurant: Restaurant, fulfillmentParams?: any[]): any[] {
    const enabledFulfillments = [];
    const {isCurbsideEnabled, isTablesideEnabled, isPickupEnabled, isDeliveryEnabled, isXSideEnabled, handoffLocation, isFridgeEnabled} = restaurant.availableStatus;
    if (isPickupEnabled && this.checkFulfillmentParams('pickup', fulfillmentParams)) {
      enabledFulfillments.push('Pickup');
    }
    if (isCurbsideEnabled && this.checkFulfillmentParams('curbside', fulfillmentParams)) {
      enabledFulfillments.push('Curbside');
    }
    if (isTablesideEnabled && (restaurant.isAcceptingOrders || !restaurant.allowScheduledPickups) && this.checkFulfillmentParams('tableside', fulfillmentParams)) {
      enabledFulfillments.push('Tableside');
      if (restaurant.isTabsEnabled) {
        enabledFulfillments.push('Tab');
      }
    }
    if (isDeliveryEnabled && this.checkFulfillmentParams('delivery', fulfillmentParams)) {
      enabledFulfillments.push('Delivery');
    }
    if (isXSideEnabled && this.checkFulfillmentParams('xside', fulfillmentParams)) {
      if (handoffLocation) {
        enabledFulfillments.push('X-Side');
      }
    }
    if (isFridgeEnabled && this.checkFulfillmentParams('fridge', fulfillmentParams)) {
      enabledFulfillments.push('Fridge')
    }
    if (!isPickupEnabled && !isCurbsideEnabled && !isTablesideEnabled && !isDeliveryEnabled && !isXSideEnabled && !isFridgeEnabled) {
      enabledFulfillments.push('Pickup');
    }
    this.enabledFulfillments = enabledFulfillments;
    return enabledFulfillments;
  }

  private checkFulfillmentParams(param: string, fulfillmentParams: any[]): boolean {
    if (fulfillmentParams != null && fulfillmentParams.length > 0) {
      return fulfillmentParams.includes(param);
    }
    return true;
  }

  private setCustomCss(customCss: string) {
    if (customCss && !this.isGroup) {
      const customData: CustomCss = JSON.parse(customCss);
      const {font, enlargeImages, itemColumns} = customData;
      if (font && font.length > 0) {
        this.formatStyleService.setGlobalFont(font);
      }
      this.itemColumns = itemColumns;
    }
  }

  public setCustomStyleService(restaurant: Restaurant) {
    this.formatStyleService.setGlobalBrandColor(restaurant.brandColor);
    this.formatStyleService.setGlobalSecondaryBrandColor(restaurant.secondaryBrandColor);
    this.formatStyleService.setGlobalBackgroundColor(restaurant.backgroundColor);
    this.formatStyleService.setGlobalSecondBackgroundColor(restaurant.secondaryBackgroundColor);
    this.formatStyleService.setGlobalBorderColor(restaurant.borderColor);
    this.formatStyleService.setGlobalPrimaryTextColor(restaurant.primaryTextColor);
    this.formatStyleService.setGlobalSecondaryTextColor(restaurant.secondaryTextColor);
    this.formatStyleService.setGlobalWarningTextColor(restaurant.warningTextColor)
  }

  private setCustomStyleServiceGroup(group: Group) {
    this.formatStyleService.setGlobalBrandColor(group.brandColor);
  }

  private stringToParams(url: string): any {
    const obj = {};
    if (url && url.split('?')[1]) {
      const urlParam = url.split('?')[1];
      const urlSeparate = urlParam.split('&');
      urlSeparate.forEach(params => {
        const properties = params.split('=');
        obj[properties[0]] = properties[1];
      });
    }
    return obj;
  }

  public clearRestaurantData(): void {
    this._curCategories$.next([]);
    this._curProducts$.next([]);
  }

  public clearRestaurants(): void {
    this._restaurants$.next([]);
  }

  public getProductsByReward(rewardId: string, locationId: string, merchantId: string) {
    this.restaurantService.getProductsByReward(rewardId, locationId, merchantId).subscribe((products)=>{
      const currentProducts = this._rewardProducts.value;
      const existingIndex = currentProducts.findIndex(item => item.id === rewardId);

      if (existingIndex > -1) {
        currentProducts[existingIndex].items = products;
      } else {
        currentProducts.push({ id: rewardId, items: products });
      }
      this._rewardProducts.next([...currentProducts]);
    });
  }

  /**
   * Generates fulfillment type items based on restaurant availability status
   * Includes full group-aware logic from checkout.component
   * @param restaurantAvailableStatus The restaurant's available status
   * @param restaurant The restaurant object
   * @param isSubscriptionCheckout Whether this is for subscription checkout
   * @param workHoursDaysInAdvance Work hours for time range validation (optional)
   * @returns Object with fulfillmentTypeItems and deliveryTypeItems arrays
   */
  public generateFulfillmentTypeItems(
    restaurantAvailableStatus: RestaurantAvailableStatus,
    restaurant: Restaurant,
    isSubscriptionCheckout: boolean = false,
    workHoursDaysInAdvance?: WorkDay[]
  ): { fulfillmentTypeItems: FulfillmentTypeItem[], deliveryTypeItems: DeliveryTypeItem[] } {
    const {
      isCurbsideEnabled,
      isTablesideEnabled,
      isPickupEnabled,
      isDeliveryEnabled,
      isXSideEnabled,
      isFridgeEnabled,
      handoffLocation,
      deliverySeller,
      deliveryCourier,
      deliverySellerMinAmount,
      deliveryCourierMinAmount,
    } = restaurantAvailableStatus;

    const fulfillmentTypeItems: any[] = [];
    const deliveryTypeItems: DeliveryTypeItem[] = [];

    // Subscription checkout only allows pickup
    if (isSubscriptionCheckout) {
      this.pickupFulfillmentType.minAmount = restaurant.minMaxRestrictions.pickup.minimum;
      this.pickupFulfillmentType.maxAmount = restaurant.minMaxRestrictions.pickup.maximum;
      fulfillmentTypeItems.push(this.pickupFulfillmentType);
      return { fulfillmentTypeItems, deliveryTypeItems };
    }

    // Pickup
    if (this.checkFulfillmentForRestaurant('Pickup', workHoursDaysInAdvance) ||
        this.checkFulfillmentForGroup(isPickupEnabled)) {
      this.pickupFulfillmentType.minAmount = restaurant.minMaxRestrictions.pickup.minimum;
      this.pickupFulfillmentType.maxAmount = restaurant.minMaxRestrictions.pickup.maximum;
      fulfillmentTypeItems.push(this.pickupFulfillmentType);
    }

    // Curbside
    if (this.checkFulfillmentForRestaurant('Curbside', workHoursDaysInAdvance) ||
        this.checkFulfillmentForGroup(isCurbsideEnabled)) {
      this.curbsideFulfillmentType.minAmount = restaurant.minMaxRestrictions.curbside.minimum;
      this.curbsideFulfillmentType.maxAmount = restaurant.minMaxRestrictions.curbside.maximum;
      fulfillmentTypeItems.push(this.curbsideFulfillmentType);
    }

    // Tableside
    if (this.checkFulfillmentForRestaurant('Tableside', workHoursDaysInAdvance) ||
        this.checkFulfillmentForGroup(isTablesideEnabled)) {
      this.tablesideFulfillmentType.minAmount = restaurant.minMaxRestrictions.tableside.minimum;
      this.tablesideFulfillmentType.maxAmount = restaurant.minMaxRestrictions.tableside.maximum;
      fulfillmentTypeItems.push(this.tablesideFulfillmentType);
    }

    // Delivery — flavor-shift locations are seller-delivery only, courier is never offered
    if ((this.checkFulfillmentForRestaurant('Delivery', workHoursDaysInAdvance) ||
        this.checkFulfillmentForGroup(isDeliveryEnabled)) &&
        (!restaurant.useFlavorShiftsTime || deliverySeller)) {
      fulfillmentTypeItems.push(this.deliveryFulfillmentType);
      if (deliverySeller) {
        // For groups, delivery types go into fulfillmentTypeItems instead of separate array
        this.isGroup
          ? fulfillmentTypeItems.push({
              title: 'Seller',
              icon: '',
              type: DeliveryType.SELLER,
              minAmount: restaurant.minMaxRestrictions.delivery.minimum,
              maxAmount: restaurant.minMaxRestrictions.delivery.maximum
            })
          : deliveryTypeItems.push({
              title: 'Seller',
              icon: '',
              type: DeliveryType.SELLER,
              minAmount: restaurant.minMaxRestrictions.delivery.minimum,
              maxAmount: restaurant.minMaxRestrictions.delivery.maximum
            });
      }
      if (deliveryCourier && !restaurant.useFlavorShiftsTime) {
        this.isGroup
          ? fulfillmentTypeItems.push({
              title: 'Courier',
              icon: '',
              type: DeliveryType.COURIER,
              minAmount: deliveryCourierMinAmount
            })
          : deliveryTypeItems.push({
              title: 'Courier',
              icon: '',
              type: DeliveryType.COURIER,
              minAmount: deliveryCourierMinAmount
            });
      }
    }

    // X-Side (Handoff)
    if (this.checkFulfillmentForRestaurant('X-Side', workHoursDaysInAdvance) ||
        this.checkFulfillmentForGroup(isXSideEnabled)) {
      if (handoffLocation) {
        const handoffTitle = handoffLocation;
        fulfillmentTypeItems.push({
          title: handoffTitle,
          icon: `icon-${handoffLocation.toLowerCase()}side`,
          type: FulfillmentType.HANDOFF,
          minAmount: restaurant.minMaxRestrictions.handoff.minimum,
          maxAmount: restaurant.minMaxRestrictions.handoff.maximum,
        });
      }
    }

    // Fridge
    if (this.checkFulfillmentForRestaurant('Fridge', workHoursDaysInAdvance)) {
      fulfillmentTypeItems.push(this.fridgeFulfillmentType);
    }

    // Group fallback: if no fulfillments enabled, add pickup
    if (this.isGroup && !isPickupEnabled && !isCurbsideEnabled && !isTablesideEnabled &&
        !isXSideEnabled && !isFridgeEnabled) {
      this.pickupFulfillmentType.minAmount = restaurant.minMaxRestrictions.pickup.minimum;
      this.pickupFulfillmentType.maxAmount = restaurant.minMaxRestrictions.pickup.maximum;
      fulfillmentTypeItems.push(this.pickupFulfillmentType);
    }

    return { fulfillmentTypeItems, deliveryTypeItems };
  }

  /**
   * Checks if a fulfillment type is enabled for the restaurant
   * Includes time range validation for Tableside when applicable
   * @param fulfillmentType The fulfillment type to check
   * @param workHoursDaysInAdvance Work hours for time range validation (optional)
   * @returns True if fulfillment is enabled
   */
  public checkFulfillmentForRestaurant(fulfillmentType: string, workHoursDaysInAdvance?: WorkDay[]): boolean {
    if (this.timeRangeParams && fulfillmentType === 'Tableside' && workHoursDaysInAdvance) {
      return (
        this.enabledFulfillments.includes(fulfillmentType) &&
        this.formatDataService.isTimeNowInWorkRange(workHoursDaysInAdvance[0])
      );
    }
    return this.enabledFulfillments.includes(fulfillmentType);
  }

  /**
   * Checks if a fulfillment type is enabled for a group
   * @param fulfillmentEnabled Whether the fulfillment is enabled in the restaurant availability status
   * @returns True if this is a group and the fulfillment is enabled
   */
  public checkFulfillmentForGroup(fulfillmentEnabled: boolean): boolean {
    return this.isGroup && fulfillmentEnabled;
  }

}
